import { LENS_LAYOUT } from '~/configs/unifiedAnalyticsTheme';

import type { Theme } from '@mui/material/styles';

import { BRAND_COLORS } from '~/components/brand/brandTokens';

import type {
  LensCalendar,
  LensCard,
  LensCardConfig,
  LensLabelFormat,
  LensMark,
  LensResolvedPeriod,
  LensSeriesSort,
  LensSortConfig,
  LensValueFormat,
  LensValueFormatKind,
} from '~/types/leLens';

import { LENS_DEFAULT_CALENDAR, MONTHS, wallClockOf } from './lensCalendar';
import { parseGrain } from './lensGrain';
import { type TimeScale, instantOf, intervalLabel, isCurrentInterval, timeScaleOf, valueLabel } from './lensTime';

/**
 * Muted neutral ramp for every series that is neither the default (slot 0)
 * nor the one highlighted series. One hue family, darkening from white/light
 * grey toward Ocean Teal rather than lightening away from it, so every step
 * clears 3:1 contrast against its card surface (Compass White in light mode,
 * #14545E in dark). The original, paler ramp (down to #F7F7F5) read as
 * invisible on a white card; confirmed with the dataviz skill's
 * validate_palette.js, see lensChart.test.ts for the exact figures.
 *
 * Deliberately short: past this many real series, buildChartModel folds the
 * tail into "Other" (see MAX_SERIES below) rather than this ramp inventing a
 * paler step that would fail contrast again.
 */
const NEUTRAL_RAMP_LIGHT = ['#3F6E75', '#5C7F86', '#7C8C90'] as const;

/** The dark-field equivalent: light greys already clear 3:1 against a dark
 *  teal card by construction, so this ramp keeps its original, wider spacing. */
const NEUTRAL_RAMP_DARK = ['#B7C4C6', '#CBD5D6', '#DCE4E4'] as const;

/**
 * Categorical series colours, assigned in fixed order and never cycled or
 * repainted when a series drops out. Slot 0 is the default series: Ocean
 * Teal on a light field, Compass White on a dark one. Coral Green never
 * appears here: it is reserved for the one series or point a card or an
 * active cross-filter selection explicitly calls out (see
 * HIGHLIGHT_SERIES_COLOR), never for an ordinary, unhighlighted series.
 */
export const SERIES_COLORS_LIGHT = [BRAND_COLORS.oceanTeal, ...NEUTRAL_RAMP_LIGHT] as const;
export const SERIES_COLORS_DARK = [BRAND_COLORS.compassWhite, ...NEUTRAL_RAMP_DARK] as const;

export const SERIES_COLORS_MAX = SERIES_COLORS_LIGHT.length;

/**
 * The one highlight colour a card ever uses: the series or point a card's
 * config names, or an active cross-filter selection picks out. The same
 * value in both modes. Coral Green never varies with theme, unlike every
 * other series colour above.
 */
export const HIGHLIGHT_SERIES_COLOR = BRAND_COLORS.coralGreen;

/** Past this a fifth hue would have to be invented, so the tail folds to Other. */
export const MAX_SERIES = SERIES_COLORS_MAX;
export const OTHER_SERIES = 'Other';
export const LENS_RATIO_PARTS = Symbol('lensRatioParts');
export type LensRatioRow = Record<string, unknown> & {
  [LENS_RATIO_PARTS]?: { numerator: number; denominator: number };
};
/** The largest categorical comparison that still reads as one scannable view. */
export const MAX_RANKED_CATEGORIES = 10;

const DEFAULT_LOCALE = 'en-AU';
/** Internal epoch-millisecond field used by Recharts time axes. */
export const LENS_TIME_AXIS_KEY = '__lensTimestamp';
/** Note shown beside the current, still-accumulating period on a chart. */

export type LensChartModel = {
  xKey: string;
  series: Array<string>;
  rows: Array<Record<string, unknown>>;
  /** How many source categories were folded into Other, for the caption. */
  foldedCount: number;
  /**
   * Palette slot per series name, ordered by name rather than by discovery or
   * magnitude. Colour follows the entity, not its rank, so a refresh or a
   * cross-filter click that changes which series is largest does not repaint
   * the series that stayed.
   */
  colorIndex: Record<string, number>;
  /**
   * The same assignment resolved to an actual colour for the caller's theme,
   * built from the validated token palette via seriesColorFor. Present only
   * when buildChartModel was given a theme.
   */
  colors?: Record<string, string>;
  /** True when chartMarkCeiling was exceeded and rows were sampled rather than drawn in full. */
  sampled: boolean;
  /** The row count before sampling, for the "sampled from N rows" footer note. */
  sourceRowCount: number;
};

export function seriesColors(isDark: boolean, overrides?: Array<string>): ReadonlyArray<string> {
  const base = isDark ? SERIES_COLORS_DARK : SERIES_COLORS_LIGHT;
  if (!overrides?.length) {
    return base;
  }
  // Overrides fill from slot 1; anything beyond falls back to the validated order.
  return base.map((color, index) => overrides[index] ?? color);
}

/**
 * Query aliases remain the durable keys for views, formats and filter bindings.
 * This only changes the business-facing label drawn in the card.
 */
export function displayLensFieldName(column: string, config?: LensCardConfig): string {
  const renamed = config?.fieldLabels?.[column]?.trim();
  return renamed || column.replace(/_/g, ' ');
}

/** Decimals a value gets when its format declares none, by kind, from the magnitude the reader sees. */
const PRECISION_DEFAULTS: Record<LensValueFormatKind, (displayedMagnitude: number) => number> = {
  currency: () => 0,
  percent: (magnitude) => decimalsForTwoSignificantFigures(magnitude),
  number: (magnitude) => (magnitude >= 100 ? 0 : 2),
};

export function formatLensValue(value: unknown, format?: LensValueFormat): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  // A number that is not finite is not a number a reader can use, and printing
  // the word NaN at them is worse than printing nothing. Text that simply does
  // not parse is different: it passes through, since a label reaching here
  // should still read as itself.
  if (typeof value === 'number' && !Number.isFinite(value)) {
    return '—';
  }
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) {
    return String(value);
  }

  const locale = format?.locale || DEFAULT_LOCALE;
  const options: Intl.NumberFormatOptions = {};

  if (format?.kind === 'currency' && format.currency) {
    options.style = 'currency';
    options.currency = format.currency;
  } else if (format?.kind === 'percent') {
    options.style = 'percent';
  }

  // Intl multiplies a percent by 100. A value that already reads as 17.08 must
  // be scaled back first, which is the usual shape of a SQL "pct" column.
  const scaled =
    format?.kind === 'percent' && (format.percentScale ?? 'percent') === 'percent' ? numeric / 100 : numeric;

  if (format?.decimals === undefined) {
    // Automatic precision follows what the reader sees, not the raw stored
    // value. A unit-scale percentage of 0.318 displays as 31.8%, for example.
    // Once the displayed number reaches three integer digits, cents and other
    // fractional noise stop helping comparison and are rounded away.
    const displayedMagnitude =
      format?.kind === 'percent' && (format.percentScale ?? 'percent') === 'unit'
        ? Math.abs(numeric * 100)
        : Math.abs(numeric);
    const automaticDecimals = PRECISION_DEFAULTS[format?.kind ?? 'number'](displayedMagnitude);
    options.maximumFractionDigits = automaticDecimals;
    if (automaticDecimals === 0) {
      options.minimumFractionDigits = 0;
    }
  } else {
    options.minimumFractionDigits = format.decimals;
    options.maximumFractionDigits = format.decimals;
  }

  const body = new Intl.NumberFormat(locale, options).format(scaled);
  return `${format?.prefix ?? ''}${body}${spacedSuffix(format?.suffix)}`;
}

/** Whole numbers keep every digit; below ten, enough decimals for two significant figures. */
export function decimalsForTwoSignificantFigures(magnitude: number): number {
  if (magnitude === 0 || magnitude >= 10) return 0;
  if (magnitude >= 1) return 1;
  return Math.min(100, 1 - Math.floor(Math.log10(magnitude)));
}

/** Splits a table currency into an anchored symbol and an automatically precise amount. */
export function accountingCurrencyParts(
  value: unknown,
  format?: LensValueFormat,
): { amount: string; symbol: string } | null {
  if (format?.kind !== 'currency' || !format.currency || value === null || value === undefined || value === '') {
    return null;
  }
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) {
    return null;
  }

  const decimals = format.decimals ?? PRECISION_DEFAULTS.currency(Math.abs(numeric));
  const parts = new Intl.NumberFormat(format.locale || DEFAULT_LOCALE, {
    currency: format.currency,
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
    style: 'currency',
  }).formatToParts(numeric);
  const symbol = parts
    .filter((part) => part.type === 'currency')
    .map((part) => part.value)
    .join('');
  if (!symbol) {
    return null;
  }
  const amount = parts
    .filter((part) => part.type !== 'currency')
    .map((part) => part.value)
    .join('')
    .trim();
  return { amount: `${format.prefix ?? ''}${amount}${spacedSuffix(format.suffix)}`, symbol };
}

/**
 * The warehouse does not attach a presentation format to every returned
 * column. Use durable analytical naming conventions as the safe default so a
 * raw result never exposes binary floating-point tails to a reader. Explicit
 * card formats still win in `fieldFormat` below.
 */
export const UNIT_FRACTION_PERCENT: LensValueFormat = { kind: 'percent', percentScale: 'unit' };

export function inferLensFieldFormat(field: string, values: ReadonlyArray<unknown> = []): LensValueFormat | undefined {
  const name = field.trim().toLocaleLowerCase();
  const tokens = name.split(/[^a-z0-9]+/).filter(Boolean);
  const hasToken = (...candidates: Array<string>) => candidates.some((candidate) => tokens.includes(candidate));
  const finiteValues = values
    .map((value) => (typeof value === 'number' ? value : Number(value)))
    .filter((value): value is number => Number.isFinite(value));

  if (hasToken('aud', 'usd', 'gbp', 'eur', 'cad', 'nzd', 'sgd')) {
    const currency = tokens.find((token) => ['aud', 'usd', 'gbp', 'eur', 'cad', 'nzd', 'sgd'].includes(token));
    return { kind: 'currency', currency: currency?.toUpperCase() ?? 'AUD' };
  }

  // A return ratio or accounting bridge is not inherently a percentage or a dollar
  // amount. Keep ambiguous units plain; explicit card/payload formats still take precedence.
  const monetaryMeasure =
    hasToken('ttv', 'aov', 'gm', 'gmv', 'revenue', 'sales', 'spend', 'cost') ||
    (hasToken('margin') && hasToken('contribution', 'amount'));
  const monetaryContributionOrAmount =
    monetaryMeasure &&
    !hasToken('ratio', 'roas', 'mroas', 'roi') &&
    (hasToken('amount') || (hasToken('contribution') && !hasToken('effect')));
  if (
    hasToken('ratio', 'roas', 'mroas', 'roi', 'effect', 'contribution') &&
    !monetaryContributionOrAmount &&
    !hasToken('pct', 'percent', 'percentage', 'share', 'conversion') &&
    !/(?:^|_)rate$/.test(name)
  ) {
    return { kind: 'number', decimals: 3 };
  }

  const percentScaleByName = hasToken('share', 'conversion')
    ? 'unit'
    : hasToken('pct', 'percent', 'percentage') || /(?:^|_)rate$/.test(name)
      ? 'percent'
      : undefined;
  if (percentScaleByName) {
    // SQL output is split between fraction-form `0.0868` and percentage-form
    // `8.68`. The actual returned values settle that ambiguity; only a field
    // with no rows falls back to the conventional percentage-form default.
    const percentScale =
      finiteValues.length > 0
        ? finiteValues.every((value) => Math.abs(value) <= 1)
          ? 'unit'
          : 'percent'
        : percentScaleByName;
    return { kind: 'percent', percentScale };
  }

  if (hasToken('rank', 'count', 'bookings', 'orders', 'packages', 'quantity', 'qty', 'rows')) {
    return { kind: 'number', decimals: 0 };
  }

  if (monetaryMeasure && !hasToken('pct', 'percent', 'rate', 'share')) {
    return { kind: 'currency', currency: 'AUD' };
  }

  if (finiteValues.length > 0 && finiteValues.every(Number.isInteger)) {
    return { kind: 'number', decimals: 0 };
  }
  return undefined;
}

/**
 * A field's own format if it has one, otherwise the card default. Tables
 * routinely mix currency, counts and percentages in one result set.
 */
export function fieldFormat(config: LensCardConfig | undefined, field: string): LensValueFormat | undefined {
  const explicit = config?.fieldFormats?.[field] ?? config?.format;
  if (explicit) {
    return explicit;
  }
  return inferLensFieldFormat(field);
}

/**
 * Only the series a combo axis actually carries can share one label. Returns
 * the common format when every named series agrees, or undefined when they
 * do not, so the axis falls back to plain numbers rather than borrowing one
 * series' unit for the others.
 */
export function sharedFieldFormat(
  config: LensCardConfig | undefined,
  seriesNames: ReadonlyArray<string>,
): LensValueFormat | undefined {
  if (!seriesNames.length) {
    return undefined;
  }
  const first = fieldFormat(config, seriesNames[0]);
  const shared = seriesNames.every((name) => JSON.stringify(fieldFormat(config, name)) === JSON.stringify(first));
  return shared ? first : undefined;
}

export function lensValueFormatter(format?: LensValueFormat) {
  return (value: unknown) => formatLensValue(value, format);
}

export function isNumeric(value: unknown): boolean {
  return (
    typeof value === 'number' || (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value)))
  );
}

/**
 * A column is a measure only when every value it carries is numeric, ignoring
 * blanks. One numeric-looking stray in an otherwise text column, such as an id
 * that happens to be all digits on one row, must not promote the whole column
 * into a series or a filter range.
 */
export function isMeasureColumn(rows: Array<Record<string, unknown>>, column: string): boolean {
  let sawValue = false;
  for (const row of rows) {
    const value = row[column];
    if (value === null || value === undefined || value === '') {
      continue;
    }
    if (!isNumeric(value)) {
      return false;
    }
    sawValue = true;
  }
  return sawValue;
}

/**
 * Matches an ISO calendar value at any grain BigQuery returns it: year-month,
 * a full date, or a date with a time component. Recognised as ordered and
 * date-like regardless of grain, so a month column sorts chronologically
 * instead of in whatever order the warehouse happened to return it.
 */
const DATE_PREFIX_RE = /^\d{4}-\d{2}(-\d{2})?([T ]|$)/;

export function isDateLikeText(value: unknown): boolean {
  if (value instanceof Date) {
    return !Number.isNaN(value.getTime());
  }
  // BigQuery can preserve DATE/DATETIME values as a small wrapper object.
  // Treat its scalar exactly like the JSON form so a temporal axis never
  // silently falls back to value ranking.
  if (value && typeof value === 'object' && 'value' in value) {
    return isDateLikeText((value as { value?: unknown }).value);
  }
  return typeof value === 'string' && DATE_PREFIX_RE.test(value.trim());
}

export function cardChartConfig({
  config,
  type,
  view,
}: Pick<LensCard, 'config' | 'view'> & Partial<Pick<LensCard, 'type'>>): LensCardConfig | undefined {
  const values = view?.values ?? [];
  const valueFormats = values.map((name) => fieldFormat(config, name));
  const firstFormat = valueFormats[0];
  const mixesUnits = valueFormats.some(
    (format) =>
      format?.kind !== firstFormat?.kind ||
      format?.currency !== firstFormat?.currency ||
      format?.percentScale !== firstFormat?.percentScale,
  );
  const usesSingleAxis = ['bar', 'stacked', 'line', 'area'].includes(type ?? '');
  // A plain bar/line/area chart owns one scale. Generated plans occasionally
  // returned a count and money together (for example Bookings beside Gross
  // sales), which rendered the count as a near-zero mark on the currency
  // axis. Keep the first, reader-named measure on ordinary charts; a genuine
  // mixed-unit comparison must opt into a combo with a secondary axis.
  const safeSeries =
    !config?.series && values.length > 1 && mixesUnits && usesSingleAxis ? [values[0]] : config?.series;

  if (!view?.x && !view?.series && !safeSeries) {
    return config;
  }
  return {
    ...config,
    ...(safeSeries ? { series: safeSeries } : {}),
    xColumn: config?.xColumn ?? view?.x,
    categoryColumn: config?.categoryColumn ?? view?.series,
  };
}

/**
 * Turns a result set into plot-ready series. Long results (month, brand,
 * revenue) are pivoted to wide, because that is the shape a GROUP BY returns
 * and therefore the shape AskRichard tends to generate.
 */
export function buildChartModel(
  rows: Array<Record<string, unknown>>,
  columns: Array<string>,
  config?: LensCardConfig,
  /** Resolves `colors` to real theme colours; omit to use `colorIndex` against a palette array instead. */
  theme?: Theme,
  /**
   * The card's full, stable series universe, from its rows before ranges and
   * cross-filter selections narrow them. When given, a present series' palette
   * slot is looked up from this list's alphabetical order rather than from the
   * rank of just the series currently on screen, so a series keeps its slot
   * whether or not its neighbours survived the same narrowing (colour follows
   * the entity, never its rank). Omitted falls back to ranking only the
   * present series, the prior behaviour, for a caller with no unfiltered rows
   * on hand yet.
   */
  allSeriesNames?: ReadonlyArray<string>,
): LensChartModel | null {
  if (!rows.length || !columns.length) {
    return null;
  }

  const numericColumns = columns.filter((column) => isMeasureColumn(rows, column));
  const textColumns = columns.filter((column) => !numericColumns.includes(column));

  // A long result normally has two labels (for example month, brand) and one
  // measure, which makes the series column obvious. An author can also choose
  // a numeric x axis such as an hour bucket. In that case there is only one
  // text column left, so use it as the category when it leaves exactly one
  // numeric measure. Without this, each category/hour row is joined into one
  // zig-zagging line instead of being pivoted into one line per category.
  const configuredX = config?.xColumn;
  const categoryColumn =
    config?.categoryColumn ??
    (textColumns.length >= 2 && numericColumns.length === 1
      ? textColumns[1]
      : configuredX &&
          textColumns.length === 1 &&
          configuredX !== textColumns[0] &&
          numericColumns.filter((column) => column !== configuredX).length === 1
        ? textColumns[0]
        : undefined);

  if (categoryColumn && columns.includes(categoryColumn)) {
    // Resolve x first: with a numeric x (such as hour), that field must not
    // also become the value merely because it is the first numeric column.
    const xKey =
      config?.xColumn ?? columns.find((column) => column !== categoryColumn && !numericColumns.includes(column));
    const valueColumn =
      config?.valueColumn ?? numericColumns.find((column) => column !== categoryColumn && column !== xKey);
    if (valueColumn && xKey) {
      const pivoted = pivotLongRows(rows, xKey, categoryColumn, valueColumn);
      return pivoted && applyOrdering(pivoted, config, theme, allSeriesNames);
    }
  }

  const xKey = config?.xColumn ?? textColumns[0] ?? columns[0];
  const explicit = config?.series?.filter((column) => columns.includes(column));
  const series = (explicit?.length ? explicit : numericColumns.filter((column) => column !== xKey)).slice(
    0,
    MAX_SERIES,
  );
  if (!xKey || !series.length) {
    return null;
  }

  const numeric = rows.map((row) => {
    const next: Record<string, unknown> = { ...row };
    series.forEach((column) => {
      next[column] = isNumeric(row[column]) ? Number(row[column]) : null;
    });
    return next;
  });

  // A wide result has no Other bucket: an excess column is dropped outright
  // rather than summed into a residual series, so there is nothing to caption.
  return applyOrdering(
    { xKey, series, rows: numeric, foldedCount: 0, colorIndex: {}, sampled: false, sourceRowCount: numeric.length },
    config,
    theme,
    allSeriesNames,
  );
}

/** Align a daily companion by position in its resolved window, retaining gaps.
 * Only a single measured line with comparable, equally long civil-day windows
 * qualifies. Authored multi-window/grouped series keep their own chart model. */
export function dailyComparisonChartModel(
  current: LensChartModel,
  comparison: LensChartModel | null,
  period: LensResolvedPeriod | undefined,
  config?: LensCardConfig,
): { model: LensChartModel; config: LensCardConfig } | null {
  if (
    !comparison ||
    !period?.comparison ||
    period.comparable === false ||
    current.series.length !== 1 ||
    comparison.series.length !== 1 ||
    comparison.series[0] !== current.series[0] ||
    comparison.xKey !== current.xKey
  ) {
    return null;
  }
  const day = (value: unknown) =>
    typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? Date.parse(`${value}T00:00:00Z`) : NaN;
  const currentStart = day(period.current.from);
  const comparisonStart = day(period.comparison.from);
  const duration = day(period.current.to) - currentStart;
  if (
    !Number.isFinite(currentStart) ||
    !Number.isFinite(comparisonStart) ||
    !Number.isFinite(duration) ||
    duration !== day(period.comparison.to) - comparisonStart
  ) {
    return null;
  }
  const measure = current.series[0];
  const currentName = `Current: ${period.label.current}`;
  const comparisonName = `Prior: ${period.label.comparison ?? 'comparison'}`;
  const rows = new Map<number, Record<string, unknown>>();
  for (const [source, start, name] of [
    [current, currentStart, currentName],
    [comparison, comparisonStart, comparisonName],
  ] as const) {
    const seen = new Set<number>();
    for (const row of source.rows) {
      const offset = day(row[source.xKey]) - start;
      if (!Number.isFinite(offset) || offset < 0 || offset > duration || seen.has(offset)) {
        return null;
      }
      seen.add(offset);
      const next = rows.get(offset) ?? {
        [current.xKey]: new Date(currentStart + offset).toISOString().slice(0, 10),
        [currentName]: null,
        [comparisonName]: null,
      };
      next[name] = row[measure];
      rows.set(offset, next);
    }
  }
  const format = fieldFormat(config, measure);
  return {
    model: {
      ...current,
      series: [currentName, comparisonName],
      rows: [...rows.entries()].sort(([a], [b]) => a - b).map(([, row]) => row),
      colorIndex: { [currentName]: 0, [comparisonName]: 1 },
      sourceRowCount: rows.size,
    },
    config: {
      ...config,
      ...(format ? { fieldFormats: { ...config?.fieldFormats, [currentName]: format, [comparisonName]: format } } : {}),
    },
  };
}

/** Pivot long rows to wide, keeping the largest series and folding the tail. */
function pivotLongRows(
  rows: Array<Record<string, unknown>>,
  xKey: string,
  categoryColumn: string,
  valueColumn: string,
): LensChartModel | null {
  const totals = new Map<string, number>();
  rows.forEach((row) => {
    const name = String(row[categoryColumn] ?? '(null)');
    const value = isNumeric(row[valueColumn]) ? Number(row[valueColumn]) : 0;
    totals.set(name, (totals.get(name) ?? 0) + Math.abs(value));
  });

  const ranked = [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([name]) => name);
  const kept = ranked.slice(0, ranked.length > MAX_SERIES ? MAX_SERIES - 1 : MAX_SERIES);
  const folded = ranked.slice(kept.length);
  const keptSet = new Set(kept);

  const buckets = new Map<string, Record<string, unknown>>();
  const ratioBuckets = new Map<string, Map<string, { numerator: number; denominator: number }>>();
  const order: Array<string> = [];
  rows.forEach((row) => {
    const x = String(row[xKey] ?? '(null)');
    if (!buckets.has(x)) {
      buckets.set(x, { [xKey]: row[xKey] ?? x });
      order.push(x);
    }
    const bucket = buckets.get(x);
    const name = String(row[categoryColumn] ?? '(null)');
    const key = keptSet.has(name) ? name : OTHER_SERIES;
    const parts = (row as LensRatioRow)[LENS_RATIO_PARTS];
    if (parts) {
      const ratios = ratioBuckets.get(x) ?? new Map();
      const total = ratios.get(key) ?? { numerator: 0, denominator: 0 };
      total.numerator += parts.numerator;
      total.denominator += parts.denominator;
      ratios.set(key, total);
      ratioBuckets.set(x, ratios);
      bucket[key] = total.denominator === 0 ? null : total.numerator / total.denominator;
      return;
    }
    const value = isNumeric(row[valueColumn]) ? Number(row[valueColumn]) : 0;
    bucket[key] = ((bucket[key] as number) ?? 0) + value;
  });

  // A category can genuinely be called "Other", and the folded tail writes to that same bucket
  // key. Appending the synthetic name on top of the real one listed it twice, so a renderer drew
  // two series reading the same merged total. One entry is right: the bucket holds the real
  // "Other" plus the tail, which is what the label already claims, and foldedCount still reports
  // how many categories went into it.
  const series = folded.length && !keptSet.has(OTHER_SERIES) ? [...kept, OTHER_SERIES] : kept;
  if (!series.length) {
    return null;
  }

  const plotted = order.map((x) => {
    const bucket = buckets.get(x);
    series.forEach((name) => {
      // A category never observed at this x is missing, not zero: null draws
      // a gap in a line, where a zero would draw a dip that never happened.
      if (bucket[name] === undefined) {
        bucket[name] = null;
      }
    });
    return bucket;
  });

  return {
    xKey,
    series,
    rows: plotted,
    foldedCount: folded.length,
    colorIndex: {},
    sampled: false,
    sourceRowCount: plotted.length,
  };
}

/**
 * Sorting is display intent, so it happens here rather than in the query.
 * Colour and sampling are resolved last, off the final row set, so neither
 * depends on how the series happen to be ranked for display.
 */
/**
 * Puts the rows in an order a person set by hand, dragging one category past
 * another. Anything the saved order does not name keeps its relative position
 * at the end, so a new value appearing in the data is added rather than
 * dropped, and a value that has gone is simply absent.
 */
export function applyCategoryOrder(
  rows: Array<Record<string, unknown>>,
  xKey: string,
  order: ReadonlyArray<string> | undefined,
): Array<Record<string, unknown>> {
  if (!order?.length) {
    return rows;
  }
  const rank = new Map(order.map((value, index) => [value, index]));
  return rows
    .map((row, index) => ({ row, index, rank: rank.get(String(row[xKey] ?? '')) }))
    .sort((a, b) => {
      if (a.rank === undefined && b.rank === undefined) {
        return a.index - b.index;
      }
      if (a.rank === undefined) {
        return 1;
      }
      if (b.rank === undefined) {
        return -1;
      }
      return a.rank - b.rank;
    })
    .map((entry) => entry.row);
}

function applyOrdering(
  model: LensChartModel,
  config?: LensCardConfig,
  theme?: Theme,
  allSeriesNames?: ReadonlyArray<string>,
): LensChartModel {
  const series = orderSeries(model, config?.seriesSort);
  // Time is sequence, never rank. A date axis remains chronological even if
  // an older card saved a value sort or hand-set category order when the same
  // field was still being treated as a generic label.
  const rows = isDateCategoryModel(model)
    ? sortRows(model.rows, { by: 'label', direction: 'asc' }, model.xKey, model.series)
    : config?.categoryOrder?.length
      ? applyCategoryOrder(model.rows, model.xKey, config.categoryOrder)
      : sortRows(model.rows, config?.sort ?? impliedSort(model), model.xKey, model.series);
  const sample = sampleChartRows(rows);
  const colorIndex = buildColorIndex(series, allSeriesNames);
  return {
    ...model,
    colorIndex,
    colors: theme ? buildColorMap(theme, series, colorIndex) : undefined,
    series,
    rows: sample.rows,
    sampled: sample.sampled,
    sourceRowCount: sample.sourceRowCount,
  };
}

/** A complete date-like x column has calendar order rather than ranking order. */
export function isDateCategoryModel(model: LensChartModel): boolean {
  const values = model.rows.map((row) => row[model.xKey]).filter((value) => value !== null && value !== undefined);
  return values.length > 0 && values.every((value) => isDateLikeText(value));
}

/**
 * Assigns each series its palette slot by name rather than by rank, so an
 * entity keeps its colour when a refresh or a cross-filter click changes
 * which series is largest. Other is a residual rather than a category and
 * always takes the last slot instead of competing for a place in the
 * alphabetical order.
 *
 * With a full series universe on hand (allSeriesNames, the card's series
 * before ranges and cross-filter selections narrowed it), each present
 * series' slot is its alphabetical rank within that whole universe, sorted
 * once, rather than within just what happens to be on screen. That is what
 * keeps a survivor's colour fixed when a narrowing drops a series that sat
 * alphabetically between it and another: ranking only the present series
 * would otherwise shift everyone after the gap. A present series absent from
 * the declared universe (a stale or renamed column) still gets a slot, past
 * every universe member's own position, rather than colliding with one.
 *
 * With no universe available, or a universe of one, the prior behaviour
 * applies: series are ranked among only those currently present, and a chart
 * resolved to exactly one series is pinned to slot 0 outright, never by the
 * name's alphabetical position. With no legend and no second series to tell
 * it apart from, an off-slot colour buys nothing there, and slot 0 is the one
 * palette entry every mode clears 3:1 against a white card without a direct
 * label to carry identity instead. That pin is deliberately not applied when
 * a universe of more than one is known: a series narrowed down to the only
 * one on screen should keep the slot it already had among its (temporarily
 * absent) neighbours, not jump to slot 0 and then jump again once they
 * return. A universe of one means the card has exactly one series and always
 * will, whatever narrowed it there: there is nothing to rank it against, so
 * it is always slot 0, the same as the no-universe case.
 */
function buildColorIndex(series: Array<string>, allSeriesNames?: ReadonlyArray<string>): Record<string, number> {
  if (allSeriesNames && allSeriesNames.length > 1) {
    const universe = [...new Set(allSeriesNames)]
      .filter((name) => name !== OTHER_SERIES)
      .sort((a, b) => a.localeCompare(b));
    const rank = new Map(universe.map((name, position) => [name, position]));
    const named = series.filter((name) => name !== OTHER_SERIES);
    const index: Record<string, number> = {};
    let overflow = universe.length;
    named.forEach((name) => {
      const known = rank.get(name);
      if (known !== undefined) {
        index[name] = known;
      } else {
        index[name] = overflow;
        overflow += 1;
      }
    });
    if (series.includes(OTHER_SERIES)) {
      index[OTHER_SERIES] = overflow;
    }
    return index;
  }

  if (series.length === 1) {
    return { [series[0]]: 0 };
  }
  const named = series.filter((name) => name !== OTHER_SERIES).sort((a, b) => a.localeCompare(b));
  const index = Object.fromEntries(named.map((name, position) => [name, position]));
  if (series.includes(OTHER_SERIES)) {
    index[OTHER_SERIES] = named.length;
  }
  return index;
}

/**
 * Colour follows the entity, not its rank, so a filter that changes the series
 * count does not repaint the survivors. A series beyond the palette folds into
 * "Other" at the call site rather than generating a new hue here.
 */
function paletteColorFor(isDark: boolean, seriesName: string, orderedNames: ReadonlyArray<string>): string {
  const palette = isDark ? SERIES_COLORS_DARK : SERIES_COLORS_LIGHT;
  const index = orderedNames.indexOf(seriesName);
  if (index >= 0 && index < palette.length) {
    return palette[index];
  }
  let hash = 0;
  for (let i = 0; i < seriesName.length; i += 1) {
    hash = (hash * 31 + seriesName.charCodeAt(i)) % 100000;
  }
  return palette[hash % palette.length];
}

/**
 * Resolves the same name-ordered assignment to real colours for one theme, so
 * a caller can look a series up directly instead of indexing a palette array.
 * The reference order matches colorIndex, so slot and colour never disagree.
 */
function buildColorMap(
  theme: Theme,
  series: Array<string>,
  colorIndex: Record<string, number>,
): Record<string, string> {
  const orderedNames = Object.keys(colorIndex).sort((a, b) => colorIndex[a] - colorIndex[b]);
  const isDark = theme.palette.mode === 'dark';
  return Object.fromEntries(series.map((name) => [name, paletteColorFor(isDark, name, orderedNames)]));
}

/**
 * Above chartMarkCeiling rows a mark per row stops being readable and starts
 * being slow, so the series is thinned to an even sample instead. The true
 * final row is always kept, since a dashed partial-day segment needs it.
 */
export function sampleChartRows<T>(
  rows: Array<T>,
  ceiling: number = LENS_LAYOUT.chartMarkCeiling,
): { rows: Array<T>; sampled: boolean; sourceRowCount: number } {
  if (rows.length <= ceiling || ceiling <= 0) {
    return { rows, sampled: false, sourceRowCount: rows.length };
  }

  const step = rows.length / ceiling;
  const sampled: Array<T> = [];
  for (let i = 0; i < ceiling; i += 1) {
    sampled.push(rows[Math.floor(i * step)]);
  }
  sampled[sampled.length - 1] = rows[rows.length - 1];

  return { rows: sampled, sampled: true, sourceRowCount: rows.length };
}

/** Footer caption for a chart drawing a sample rather than every row. */
export function sampledSeriesCaption(sourceRowCount: number): string | undefined {
  if (sourceRowCount <= LENS_LAYOUT.chartMarkCeiling) {
    return undefined;
  }
  return `Sampled from ${formatLensValue(sourceRowCount)} rows.`;
}

/** Footer caption for a chart that folded excess categories into Other. */
export function foldedCategoriesCaption(foldedCount: number): string | undefined {
  if (foldedCount <= 0) {
    return undefined;
  }
  return `${formatLensValue(foldedCount)} ${foldedCount === 1 ? 'category' : 'categories'} folded into Other.`;
}

/**
 * A date or number axis has an inherent order, and BigQuery does not promise
 * one without an ORDER BY. Left as it arrived, a time series is drawn as a
 * scribble, so an ordered axis is sorted unless the card says otherwise.
 */
function impliedSort(model: LensChartModel): LensSortConfig | undefined {
  const values = model.rows.map((row) => row[model.xKey]).filter((value) => value !== null && value !== undefined);
  if (values.length < 2) {
    return undefined;
  }

  const ordered = values.every((value) => isNumeric(value)) || values.every((value) => isDateLikeText(value));

  return ordered ? { by: 'label', direction: 'asc' } : undefined;
}

/**
 * The first series is drawn at the base of a stack. Other is a residual rather
 * than a category, so it is pinned to the far end whatever the ordering: left
 * in the ranking it would read as a real category, and sorted alphabetically it
 * would land wherever "O" happens to fall.
 */
function orderSeries(model: LensChartModel, seriesSort?: LensSeriesSort): Array<string> {
  if (!seriesSort || seriesSort === 'none' || model.series.length < 2) {
    return model.series;
  }

  const hasOther = model.series.includes(OTHER_SERIES);
  const ranked = model.series.filter((name) => name !== OTHER_SERIES);

  if (seriesSort === 'alpha') {
    ranked.sort((a, b) => a.localeCompare(b));
  } else {
    const total = (name: string) =>
      model.rows.reduce((sum, row) => sum + (isNumeric(row[name]) ? Math.abs(Number(row[name])) : 0), 0);
    ranked.sort((a, b) => total(b) - total(a));
    if (seriesSort === 'smallest') {
      ranked.reverse();
    }
  }

  return hasOther ? [...ranked, OTHER_SERIES] : ranked;
}

/**
 * Orders rows by label or by plotted value. Exported so table cards sort the
 * same way charts do.
 */
export function sortRows(
  rows: Array<Record<string, unknown>>,
  sort: LensSortConfig | undefined,
  labelKey: string,
  valueKeys: Array<string>,
): Array<Record<string, unknown>> {
  if (!sort || sort.by === 'none') {
    return rows;
  }
  const direction = sort.direction ?? (sort.by === 'value' ? 'desc' : 'asc');
  const sign = direction === 'asc' ? 1 : -1;
  const keys = sort.series ? [sort.series] : valueKeys;

  const rowValue = (row: Record<string, unknown>) =>
    keys.reduce((sum, key) => sum + (isNumeric(row[key]) ? Number(row[key]) : 0), 0);

  return [...rows].sort((a, b) => {
    if (sort.by === 'value') {
      return sign * (rowValue(a) - rowValue(b));
    }

    const left = a[labelKey];
    const right = b[labelKey];
    const leftInstant = instantOf(left);
    const rightInstant = instantOf(right);
    if (leftInstant !== undefined && rightInstant !== undefined) {
      return sign * (leftInstant - rightInstant);
    }
    if (isNumeric(left) && isNumeric(right)) {
      return sign * (Number(left) - Number(right));
    }
    return sign * String(left ?? '').localeCompare(String(right ?? ''));
  });
}

/**
 * Returns the exact rows a high-cardinality comparison renders.
 *
 * Shared by the option builder and its interaction layer so an ECharts
 * `dataIndex`, the keyboard target beside it and a drag operation always name
 * the same category. An authored sort or hand order has already been applied
 * by buildChartModel and remains authoritative; otherwise value-descending is
 * the useful default for a comparison.
 */
export function rankedCategoryRows(
  model: LensChartModel,
  config?: LensCardConfig,
  limit: number = MAX_RANKED_CATEGORIES,
): Array<Record<string, unknown>> {
  // A temporal bar chart is a sequence, not a leaderboard. Preserve every
  // date and let the axis thin labels; dropping all but ten days changes the
  // question the chart answers.
  if (isDateCategoryModel(model) || model.rows.length <= limit || limit <= 0) {
    return model.rows;
  }
  const ordered =
    config?.categoryOrder?.length || config?.sort
      ? model.rows
      : sortRows(model.rows, { by: 'value', direction: 'desc' }, model.xKey, model.series);
  return ordered.slice(0, limit);
}

const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * Recharts treats timestamp strings as evenly-spaced categories. For a line chart whose complete
 * x column is date-like, add an epoch field that can drive a continuous time scale instead.
 */
export function prepareLensTimeAxis(
  rows: Array<Record<string, unknown>>,
  xKey: string,
  calendar: LensCalendar = LENS_DEFAULT_CALENDAR,
): { rows: Array<Record<string, unknown>>; xKey: string; isTimeAxis: boolean; scale?: TimeScale } {
  const timed = timeScaleOf(
    rows.map((row) => row[xKey]),
    calendar,
    xKey,
    parseGrain(xKey)?.grain,
  );
  if (!timed) {
    return { rows, xKey, isTimeAxis: false };
  }
  return {
    rows: rows.map((row, index) => ({ ...row, [LENS_TIME_AXIS_KEY]: timed.instants[index] })),
    xKey: LENS_TIME_AXIS_KEY,
    isTimeAxis: true,
    scale: timed.scale,
  };
}

/**
 * A date as the rest of the surface writes one: the Sydney day, and a month of
 * three letters. Intl's own short month is not three letters in en-AU, it
 * renders June, July and Sept in full, so the month comes from MONTHS.
 */
export function formatLensDayLabel(value: Date | string | number, includeYear = false): string {
  const instant = typeof value === 'number' ? value : instantOf(value);
  return instant === undefined
    ? String(value ?? '—')
    : intervalLabel(instant, 'day', includeYear ? { yearInView: 'always' } : {});
}

export function formatLensDateCategoryLabel(
  value: unknown,
  calendar: LensCalendar = LENS_DEFAULT_CALENDAR,
  column = '',
): string {
  return valueLabel(value, calendar, column) ?? formatLensLabel(value);
}

/** Renders a fixed set of patterns. Tokens are replaced in one pass. */
export function formatLensLabel(
  value: unknown,
  format?: LensLabelFormat,
  calendar: LensCalendar = LENS_DEFAULT_CALENDAR,
): string {
  if (value === null || value === undefined) {
    return '(null)';
  }
  if ((format?.kind !== 'date' && format?.kind !== 'time') || !format.pattern) {
    return String(value);
  }

  const instant = instantOf(value, calendar);
  if (instant === undefined) {
    return String(value);
  }
  const wall = wallClockOf(new Date(instant), calendar.zone);

  if (format.kind === 'time') {
    const hours = wall.hour;
    const minutes = wall.minute;
    const seconds = wall.second;
    const parts: Record<string, string> = {
      HH: String(hours).padStart(2, '0'),
      H: String(hours),
      mm: String(minutes).padStart(2, '0'),
      m: String(minutes),
      ss: String(seconds).padStart(2, '0'),
      s: String(seconds),
    };
    return format.pattern.replace(/HH|H|mm|m|ss|s/g, (token) => parts[token] ?? token);
  }

  const day = wall.day;
  const month = wall.month - 1;
  const year = wall.year;
  const parts: Record<string, string> = {
    yyyy: String(year),
    yy: String(year).slice(-2),
    MMMM: MONTHS_LONG[month],
    MMM: MONTHS[month],
    MM: String(month + 1).padStart(2, '0'),
    M: String(month + 1),
    dd: String(day).padStart(2, '0'),
    d: String(day),
  };

  // Longest token first, so MMMM is not consumed as MMM followed by M.
  return format.pattern.replace(/yyyy|yy|MMMM|MMM|MM|M|dd|d/g, (token) => parts[token] ?? token);
}

/** How far apart two series' scales must be before they need separate axes. */
const SCALE_GAP = 20;

export type ComboAxes = {
  secondary: Array<string>;
  marks: Record<string, LensMark>;
  /** The label for the left axis, or undefined when its series do not share one format. */
  primaryFormat: LensValueFormat | undefined;
  /** The label for the right axis, or undefined when its series do not share one format. Never
   *  just the first secondary series' format: a right axis mixing a percent and a count has no
   *  single honest label. */
  secondaryFormat: LensValueFormat | undefined;
};

/**
 * Works out which series a combo should put on its second axis, for a card
 * that asked for one without saying how. Left to the shared axis, a percentage
 * beside a currency total draws as a zero-height mark, which is the failure a
 * combo exists to avoid.
 */
export function inferComboAxes(model: LensChartModel, config?: LensCardConfig): ComboAxes {
  const declared = (config?.secondaryAxis ?? []).filter((name) => model.series.includes(name));
  const magnitude = (name: string) =>
    model.rows.reduce((peak, row) => Math.max(peak, isNumeric(row[name]) ? Math.abs(Number(row[name])) : 0), 0);

  const ranked = [...model.series].sort((a, b) => magnitude(b) - magnitude(a));
  const primary = ranked[0];
  const primaryScale = primary ? magnitude(primary) : 0;

  // A series an order of magnitude or more below the largest cannot share its
  // axis and stay visible.
  const secondary = declared.length
    ? declared
    : ranked.filter((name) => name !== primary && primaryScale > 0 && magnitude(name) * SCALE_GAP < primaryScale);
  const primarySeries = model.series.filter((name) => !secondary.includes(name));

  const marks: Record<string, LensMark> = {};
  for (const name of model.series) {
    const declaredMark = config?.seriesMarks?.[name];
    // A line over bars is the readable convention for a second scale.
    marks[name] = declaredMark ?? (secondary.includes(name) ? 'line' : 'bar');
  }

  return {
    secondary,
    marks,
    primaryFormat: sharedFieldFormat(config, primarySeries),
    secondaryFormat: sharedFieldFormat(config, secondary),
  };
}

/** Whether a chart with this many series should draw a legend. */
export function shouldShowLegend(seriesCount: number): boolean {
  return seriesCount >= 2;
}

/**
 * Direct end-of-line labels only stay legible up to a handful of series: past
 * that they overlap and a legend reads better. Below the light palette's own
 * separation floor, colour alone cannot carry identity, so this is not
 * cosmetic once a card has more than one series.
 */
export function shouldShowDirectLabels(seriesCount: number): boolean {
  return seriesCount >= 2 && seriesCount <= 4;
}

export type LensMarkEmphasis = 'normal' | 'selected' | 'dimmed';

/**
 * How one mark should read against an active cross-filter selection on the
 * exact column it belongs to. 'normal' whenever that column carries no
 * selection at all, so a chart nobody has clicked on never looks dimmed.
 */
export function markEmphasis(value: string, selectedValues: ReadonlyArray<string> | undefined): LensMarkEmphasis {
  if (!selectedValues || selectedValues.length === 0) {
    return 'normal';
  }
  return selectedValues.includes(value) ? 'selected' : 'dimmed';
}

/**
 * The column a bar or stacked chart's own click sets, worked out the same way
 * a click on one of its marks does: by series when a series is named and the
 * data actually pivots into more than one, otherwise by the x axis. A
 * cross-filter selection on this exact column is what a chart needs to
 * recognise so it can highlight the clicked mark instead of narrowing every
 * other one off the canvas.
 */
export function chartCrossFilterColumn(
  rows: Array<Record<string, unknown>>,
  columns: Array<string>,
  config: LensCardConfig | undefined,
  view: { x?: string; series?: string } | undefined,
): string | undefined {
  const model = buildChartModel(rows, columns, cardChartConfig({ config, view }));
  if (!model) {
    return undefined;
  }
  const bySeries = Boolean(view?.series) && model.series.length > 1;
  return bySeries ? view?.series : (view?.x ?? model.xKey);
}

export function partialPeriodIndex(
  rows: Array<Record<string, unknown>>,
  scale: TimeScale | undefined,
  now: number = Date.now(),
): number {
  const lastIndex = rows.length - 1;
  if (!scale || lastIndex < 1) {
    return -1;
  }
  return isCurrentInterval(Number(rows[lastIndex][LENS_TIME_AXIS_KEY]), scale.unit, scale.calendar, now)
    ? lastIndex
    : -1;
}

function spacedSuffix(suffix = ''): string {
  return /^\p{L}/u.test(suffix) ? ` ${suffix}` : suffix;
}
